import { useMutation, useQuery, useQueryClient, type UseQueryResult } from '@tanstack/react-query';
import type {
  CriterionColumns,
  ImportColumnMapping,
  ImportListItem,
  ImportRowFilter,
  ImportRowList,
  ImportRowView,
  ImportView,
} from '@nest-recruit/shared';
import { ApiError, apiGet, apiPost, apiPut, request } from '../../../lib/api-client';

// IMP-01 and IMP-02 (R4). `['imports']` is the root key the API's realtime hint names, so an
// import's progress arrives with no polling (non-negotiable #12). Its applications also move
// the stage bar and the pipeline, which `['applications']` already covers.

export const IMPORTS_KEY = ['imports'] as const;

export function importKey(importId: string): readonly unknown[] {
  return [...IMPORTS_KEY, 'detail', importId];
}

function importListKey(jobId: string): readonly unknown[] {
  return [...IMPORTS_KEY, 'list', jobId];
}

export function useImports(jobId: string): UseQueryResult<readonly ImportListItem[], ApiError> {
  return useQuery<readonly ImportListItem[], ApiError>({
    queryKey: importListKey(jobId),
    queryFn: () => apiGet<readonly ImportListItem[]>(`/jobs/${jobId}/imports`),
    placeholderData: (previous) => previous,
  });
}

export function useImport(importId: string): UseQueryResult<ImportView, ApiError> {
  return useQuery<ImportView, ApiError>({
    queryKey: importKey(importId),
    queryFn: () => apiGet<ImportView>(`/imports/${importId}`),
    placeholderData: (previous) => (previous?.id === importId ? previous : undefined),
  });
}

export function useImportRows(importId: string, filter: ImportRowFilter, page: number, isEnabled: boolean): UseQueryResult<ImportRowList, ApiError> {
  return useQuery<ImportRowList, ApiError>({
    queryKey: [...IMPORTS_KEY, 'rows', importId, { filter, page }],
    queryFn: () => apiGet<ImportRowList>(`/imports/${importId}/rows`, { query: { filter, page } }),
    placeholderData: (previous) => previous,
    enabled: isEnabled,
  });
}

/** F22.01: the file goes up; the import comes back already matched as far as the app can guess. */
export function useUploadImport(jobId: string) {
  const queryClient = useQueryClient();
  return useMutation<ImportView, ApiError, { readonly file: File; readonly source: string }>({
    mutationFn: async ({ file, source }) => {
      const body = new FormData();
      body.append('file', file);
      return (await request<ImportView>(`/jobs/${jobId}/imports`, { method: 'POST', body, query: { source } })).data;
    },
    onSuccess: async (view) => {
      queryClient.setQueryData(importKey(view.id), view);
      await queryClient.invalidateQueries({ queryKey: importListKey(jobId) });
    },
  });
}

export interface MappingChange {
  readonly columns: ImportColumnMapping;
  readonly criteria: CriterionColumns;
  readonly source: string;
}

export function saveImportMapping(importId: string, change: MappingChange, version: number): Promise<ImportView> {
  return apiPut<ImportView>(`/imports/${importId}/mapping`, { ...change, version });
}

export function chooseImportSheet(importId: string, sheet: string, version: number): Promise<ImportView> {
  return apiPut<ImportView>(`/imports/${importId}/sheet`, { sheet, version });
}

export function startImport(importId: string, version: number): Promise<ImportView> {
  return apiPost<ImportView>(`/imports/${importId}/start`, { version });
}

export function cancelImport(importId: string, version: number): Promise<ImportView> {
  return apiPost<ImportView>(`/imports/${importId}/cancel`, { version });
}

export function discardImport(importId: string, version: number): Promise<ImportView> {
  return apiPost<ImportView>(`/imports/${importId}/discard`, { version });
}

export function retryImportRow(importId: string, rowId: string): Promise<ImportRowView> {
  return apiPost<ImportRowView>(`/imports/${importId}/rows/${rowId}/retry`);
}

export async function uploadImportRowCv(importId: string, rowId: string, file: File): Promise<ImportRowView> {
  const body = new FormData();
  body.append('cv', file);
  return (await request<ImportRowView>(`/imports/${importId}/rows/${rowId}/cv`, { method: 'POST', body })).data;
}
